import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Plus, Edit, Trash2, Save, RefreshCw, GripVertical, 
  Lightbulb, Search, Shield, Palette, Book, Globe, Video, 
  Users, FileText, Megaphone, Circle, Rocket, Target, Award,
  Zap, Star, Heart, Flag, CheckCircle, Settings, ExternalLink, Link
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

// Available icons for timeline steps
const AVAILABLE_ICONS = [
  { name: 'Lightbulb', icon: Lightbulb, label: 'Ampoule' },
  { name: 'Search', icon: Search, label: 'Recherche' },
  { name: 'Shield', icon: Shield, label: 'Bouclier' },
  { name: 'Palette', icon: Palette, label: 'Palette' },
  { name: 'Book', icon: Book, label: 'Livre' },
  { name: 'Globe', icon: Globe, label: 'Globe' },
  { name: 'Video', icon: Video, label: 'Vidéo' },
  { name: 'Users', icon: Users, label: 'Utilisateurs' },
  { name: 'FileText', icon: FileText, label: 'Document' },
  { name: 'Megaphone', icon: Megaphone, label: 'Mégaphone' },
  { name: 'Rocket', icon: Rocket, label: 'Fusée' },
  { name: 'Target', icon: Target, label: 'Cible' },
  { name: 'Award', icon: Award, label: 'Récompense' },
  { name: 'Zap', icon: Zap, label: 'Éclair' },
  { name: 'Star', icon: Star, label: 'Étoile' },
  { name: 'Heart', icon: Heart, label: 'Cœur' },
  { name: 'Flag', icon: Flag, label: 'Drapeau' },
  { name: 'CheckCircle', icon: CheckCircle, label: 'Validé' },
  { name: 'Settings', icon: Settings, label: 'Paramètres' },
  { name: 'Circle', icon: Circle, label: 'Cercle' },
];

// Available phases
const PHASES = [
  { id: 'Fondation', color: 'bg-amber-500' },
  { id: 'Identité', color: 'bg-pink-500' },
  { id: 'Digital', color: 'bg-cyan-500' },
  { id: 'Déploiement', color: 'bg-purple-500' },
  { id: 'Croissance', color: 'bg-green-500' },
];

const getIconComponent = (iconName) => {
  const found = AVAILABLE_ICONS.find(i => i.name === iconName);
  return found ? found.icon : Circle;
};

const CMSTimelinePage = () => {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, step: null });
  const [draggedItem, setDraggedItem] = useState(null);

  useEffect(() => {
    loadSteps();
  }, []);

  const loadSteps = async () => {
    try {
      const res = await api.get('/cms/timeline');
      setSteps(res.data || []);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStep = async () => {
    const { step } = editModal;
    setSaving(true);
    try {
      if (step.id && !step.id.startsWith('new-')) {
        await api.put(`/cms/timeline/${step.id}`, step);
      } else {
        await api.post('/cms/timeline', step);
      }
      toast.success('Étape enregistrée');
      loadSteps();
      setEditModal({ open: false, step: null });
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStep = async (stepId) => {
    if (!window.confirm('Supprimer cette étape ?')) return;
    try {
      await api.delete(`/cms/timeline/${stepId}`);
      toast.success('Étape supprimée');
      loadSteps();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const openNewStep = () => {
    setEditModal({
      open: true,
      step: {
        id: `new-${Date.now()}`,
        name: '',
        description: '',
        icon: 'Circle',
        phase: 'Fondation',
        order: steps.length + 1
      }
    });
  };

  const handleDragStart = (e, step) => {
    setDraggedItem(step);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, targetStep) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === targetStep.id) return;

    const newSteps = [...steps];
    const dragIndex = newSteps.findIndex(s => s.id === draggedItem.id);
    const dropIndex = newSteps.findIndex(s => s.id === targetStep.id);
    
    newSteps.splice(dragIndex, 1);
    newSteps.splice(dropIndex, 0, draggedItem);
    
    // Update order
    const reorderedIds = newSteps.map(s => s.id);
    setSteps(newSteps.map((s, idx) => ({ ...s, order: idx + 1 })));
    
    try {
      await api.put('/cms/timeline/reorder', { steps: reorderedIds });
      toast.success('Ordre mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la réorganisation');
      loadSteps();
    }
    setDraggedItem(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  // Group steps by phase
  const stepsByPhase = PHASES.map(phase => ({
    ...phase,
    steps: steps.filter(s => s.phase === phase.id)
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Timeline de la Communication</h1>
          <p className="text-slate-500">
            Gérez les étapes de la chronologie de communication d'entreprise
          </p>
        </div>
        <Button onClick={openNewStep} className="bg-gradient-to-r from-cyan-500 to-blue-600">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle étape
        </Button>
      </div>

      {/* Timeline by Phase */}
      <div className="space-y-6">
        {stepsByPhase.map((phase) => (
          <Card key={phase.id}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className={cn("w-4 h-4 rounded-full", phase.color)} />
                <CardTitle className="text-lg">{phase.id}</CardTitle>
                <Badge variant="secondary">{phase.steps.length} étapes</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {phase.steps.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Aucune étape dans cette phase</p>
              ) : (
                <div className="space-y-2">
                  {phase.steps.sort((a, b) => a.order - b.order).map((step) => {
                    const IconComponent = getIconComponent(step.icon);
                    return (
                      <div
                        key={step.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, step)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, step)}
                        className={cn(
                          "flex items-center gap-4 p-4 bg-slate-50 rounded-lg border border-transparent hover:border-cyan-200 hover:bg-cyan-50/50 transition-all cursor-move",
                          draggedItem?.id === step.id && "opacity-50"
                        )}
                      >
                        <GripVertical className="w-5 h-5 text-slate-400" />
                        <div className={cn("p-2 rounded-lg text-white", phase.color)}>
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{step.name}</p>
                          <p className="text-sm text-slate-500">{step.description}</p>
                          {step.link_url && (
                            <a 
                              href={step.link_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-cyan-600 hover:text-cyan-700 mt-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              {step.link_text || 'Voir le lien'}
                            </a>
                          )}
                        </div>
                        <Badge variant="outline">Étape {step.order}</Badge>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditModal({ open: true, step })}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteStep(step.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* All Steps Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Aperçu global ({steps.length} étapes)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {steps.sort((a, b) => a.order - b.order).map((step, idx) => {
              const phase = PHASES.find(p => p.id === step.phase);
              return (
                <Badge key={step.id} variant="outline" className="py-1.5">
                  <span className={cn("w-2 h-2 rounded-full mr-2", phase?.color || 'bg-slate-400')} />
                  {idx + 1}. {step.name}
                </Badge>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, step: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editModal.step?.id?.startsWith('new-') ? 'Nouvelle étape' : 'Modifier l\'étape'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nom de l'étape</Label>
              <Input
                value={editModal.step?.name || ''}
                onChange={(e) => setEditModal({ ...editModal, step: { ...editModal.step, name: e.target.value } })}
                placeholder="Création du logo"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={editModal.step?.description || ''}
                onChange={(e) => setEditModal({ ...editModal, step: { ...editModal.step, description: e.target.value } })}
                placeholder="Identité visuelle de base"
              />
            </div>

            <div className="space-y-2">
              <Label>Phase</Label>
              <div className="flex flex-wrap gap-2">
                {PHASES.map((phase) => (
                  <Button
                    key={phase.id}
                    variant={editModal.step?.phase === phase.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setEditModal({ ...editModal, step: { ...editModal.step, phase: phase.id } })}
                    className={editModal.step?.phase === phase.id ? phase.color : ''}
                  >
                    {phase.id}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Icône</Label>
              <div className="grid grid-cols-5 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg">
                {AVAILABLE_ICONS.map(({ name, icon: Icon, label }) => (
                  <button
                    key={name}
                    onClick={() => setEditModal({ ...editModal, step: { ...editModal.step, icon: name } })}
                    className={cn(
                      "p-2 rounded-lg flex flex-col items-center gap-1 transition-all",
                      editModal.step?.icon === name 
                        ? "bg-cyan-500 text-white" 
                        : "bg-slate-50 hover:bg-slate-100 text-slate-600"
                    )}
                    title={label}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-[10px]">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Ordre</Label>
              <Input
                type="number"
                value={editModal.step?.order || 1}
                onChange={(e) => setEditModal({ ...editModal, step: { ...editModal.step, order: parseInt(e.target.value) } })}
                min={1}
              />
            </div>

            {/* External Link Section */}
            <div className="space-y-3 pt-3 border-t">
              <Label className="flex items-center gap-2">
                <Link className="w-4 h-4 text-cyan-500" />
                Lien externe (optionnel)
              </Label>
              <div className="space-y-2">
                <Input
                  value={editModal.step?.link_url || ''}
                  onChange={(e) => setEditModal({ ...editModal, step: { ...editModal.step, link_url: e.target.value } })}
                  placeholder="https://exemple.com/page"
                  type="url"
                />
                <Input
                  value={editModal.step?.link_text || ''}
                  onChange={(e) => setEditModal({ ...editModal, step: { ...editModal.step, link_text: e.target.value } })}
                  placeholder="Texte du lien (ex: En savoir plus)"
                />
              </div>
              {editModal.step?.link_url && (
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" />
                  Le lien s&apos;ouvrira dans un nouvel onglet
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, step: null })}>
              Annuler
            </Button>
            <Button onClick={handleSaveStep} disabled={saving}>
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSTimelinePage;
